import React, { useState, useEffect } from 'react';
import { getAuth, reauthenticateWithCredential, updatePassword, EmailAuthProvider } from 'firebase/auth';
import { db } from '../firebase/firestore'; // adjust the path as needed
import { collection, getDocs } from 'firebase/firestore';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [activeTab, setActiveTab] = useState('changePassword');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    setCurrentUser(auth.currentUser);
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoadingAdmins(true);
      const querySnapshot = await getDocs(collection(db, "admins"));
      const adminsList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      
      // Добавляем текущего пользователя, если он не в списке
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user && user.email) {
        const userInList = adminsList.find(admin => admin.email === user.email);
        if (!userInList) {
          adminsList.unshift({
            id: 'current-user',
            email: user.email,
            name: user.displayName || 'Текущий пользователь',
            role: 'admin',
            createdAt: new Date(),
            status: 'active',
            isCurrentUser: true
          });
        } else {
          // Помечаем текущего пользователя
          userInList.isCurrentUser = true;
        }
      }
      
      setAdmins(adminsList);
    } catch (error) {
      console.error('Ошибка при загрузке администраторов:', error);
      
      // Если коллекция не существует или произошла ошибка,
      // показываем только текущего пользователя
      const auth = getAuth();
      const user = auth.currentUser;
      if (user && user.email) {
        setAdmins([{
          id: 'current-user',
          email: user.email,
          name: user.displayName || 'Текущий пользователь',
          role: 'admin',
          createdAt: new Date(),
          status: 'active',
          isCurrentUser: true
        }]);
      }
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    // Валидация
    if (newPassword !== confirmPassword) {
      setErrorMessage('Новый пароль и подтверждение не совпадают');
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage('Пароль должен содержать не менее 6 символов');
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user || !user.email) {
      setErrorMessage('Пользователь не найден');
      return;
    }

    try {
      setLoading(true);

      // Реаутентификация пользователя
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Обновление пароля
      await updatePassword(user, newPassword);

      setSuccessMessage('Пароль успешно изменен!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Ошибка при смене пароля:', error);
      
      switch (error.code) {
        case 'auth/wrong-password':
          setErrorMessage('Текущий пароль неверен');
          break;
        case 'auth/requires-recent-login':
          setErrorMessage('Требуется повторный вход. Пожалуйста, выйдите и войдите снова');
          break;
        case 'auth/weak-password':
          setErrorMessage('Пароль слишком слабый. Используйте более сложный пароль');
          break;
        default:
          setErrorMessage(`Ошибка: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Функция для форматирования даты
  const formatDate = (date) => {
    if (!date) return 'Сегодня';
    
    try {
      const now = new Date();
      const inputDate = new Date(date);
      
      // Если дата сегодня
      if (inputDate.toDateString() === now.toDateString()) {
        return 'Сегодня';
      }
      
      // Если дата вчера
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      if (inputDate.toDateString() === yesterday.toDateString()) {
        return 'Вчера';
      }
      
      // Форматируем дату
      return inputDate.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Неизвестная дата';
    }
  };

  return (
    <div className="w-full flex flex-col h-[100vh] scroller overflow-y-auto pt-4 pb-[150px]">
      <div className='w-full flex flex-col space-y-4 max-w-4xl mx-auto'>
        
        {/* Заголовок и табы */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold text-white">Управление доступом</h1>
          
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('changePassword')}
              className={`px-4 py-2 rounded-[6px] font-medium transition-colors ${activeTab === 'changePassword' ? 'bg-[#f5bb5f] text-black' : 'bg-[#4b4b4b] text-white hover:bg-[#5b5b5b]'}`}
            >
              Смена пароля
            </button>
            <button
              onClick={() => {
                setActiveTab('viewAdmins');
                fetchAdmins();
              }}
              className={`px-4 py-2 rounded-[6px] font-medium transition-colors ${activeTab === 'viewAdmins' ? 'bg-[#f5bb5f] text-black' : 'bg-[#4b4b4b] text-white hover:bg-[#5b5b5b]'}`}
            >
              Пользователи с доступом ({admins.length})
            </button>
          </div>
        </div>

        {/* Вкладка смены пароля */}
        {activeTab === 'changePassword' && (
          <div className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className='flex flex-col gap-1'>
                <label className='text-[13px] pl-1 pb-[2px] font-medium text-white'>
                  Текущий пароль
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Введите текущий пароль"
                  className="bg-[#4b4b4b] w-full placeholder:text-[#b9b9b9] text-[#e0e0e0] placeholder:text-[12px] text-[13px] placeholder:font-light h-[55px] border-none outline-none rounded-[10px] flex items-center px-6"
                  required
                  disabled={loading}
                />
              </div>

              <div className='flex flex-col gap-1'>
                <label className='text-[13px] pl-1 pb-[2px] font-medium text-white'>
                  Новый пароль
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Введите новый пароль"
                  className="bg-[#4b4b4b] w-full placeholder:text-[#b9b9b9] text-[#e0e0e0] placeholder:text-[12px] text-[13px] placeholder:font-light h-[55px] border-none outline-none rounded-[10px] flex items-center px-6"
                  required
                  disabled={loading}
                />
              </div>

              <div className='flex flex-col gap-1'>
                <label className='text-[13px] pl-1 pb-[2px] font-medium text-white'>
                  Подтвердите новый пароль
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Повторите новый пароль"
                  className="bg-[#4b4b4b] w-full placeholder:text-[#b9b9b9] text-[#e0e0e0] placeholder:text-[12px] text-[13px] placeholder:font-light h-[55px] border-none outline-none rounded-[10px] flex items-center px-6"
                  required
                  disabled={loading}
                />
              </div>

              {errorMessage && (
                <div className="bg-red-500/20 border border-red-500 text-red-300 rounded-[10px] p-4 text-sm">
                  {errorMessage}
                </div>
              )}

              {successMessage && (
                <div className="bg-green-500/20 border border-green-500 text-green-300 rounded-[10px] p-4 text-sm">
                  {successMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`${loading ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#f5bb5f] hover:bg-[#e5ab4f]'} font-semibold text-[15px] rounded-[6px] w-full h-fit px-4 py-3 text-[#000] transition-colors`}
              >
                {loading ? 'Изменение пароля...' : 'Изменить пароль'}
              </button>
            </form>

            {/* Информация о текущем пользователе */}
            <div className="p-4 bg-[#4b4b4b] rounded-[10px]">
              <h3 className="text-white font-semibold mb-3">Информация о текущем пользователе:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center text-gray-300">
                  <span>Email:</span>
                  <span className="font-medium bg-[#f5bb5f]/20 text-[#f5bb5f] px-2 py-1 rounded">
                    {currentUser?.email || 'Не определен'}
                  </span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>ID пользователя:</span>
                  <span className="font-medium text-xs text-gray-400">{currentUser?.uid || 'Не определен'}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Последний вход:</span>
                  <span className="font-medium">
                    {currentUser?.metadata?.lastSignInTime 
                      ? new Date(currentUser.metadata.lastSignInTime).toLocaleDateString('ru-RU', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Неизвестно'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Вкладка просмотра администраторов */}
        {activeTab === 'viewAdmins' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Пользователи с административным доступом</h2>
              <button
                onClick={fetchAdmins}
                disabled={loadingAdmins}
                className="bg-[#4b4b4b] hover:bg-[#5b5b5b] text-white px-4 py-2 rounded-[6px] text-sm flex items-center gap-2 transition-colors"
              >
                {loadingAdmins ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Загрузка...
                  </>
                ) : (
                  'Обновить список'
                )}
              </button>
            </div>

            {loadingAdmins ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#f5bb5f]"></div>
                <p className="text-gray-400 mt-2">Загрузка списка пользователей...</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-[10px] border border-[#5b5b5b]">
                  <table className="w-full bg-[#4b4b4b]">
                    <thead>
                      <tr className="bg-[#3b3b3b]">
                        <th className="text-left py-3 px-4 text-white font-medium text-sm">#</th>
                        <th className="text-left py-3 px-4 text-white font-medium text-sm">Пользователь</th>
                        <th className="text-left py-3 px-4 text-white font-medium text-sm">Роль</th>
                        <th className="text-left py-3 px-4 text-white font-medium text-sm">Дата добавления</th>
                        <th className="text-left py-3 px-4 text-white font-medium text-sm">Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map((admin, index) => (
                        <tr 
                          key={admin.id} 
                          className={`border-t border-[#5b5b5b] ${admin.isCurrentUser ? 'bg-[#5b4b1f]' : ''}`}
                        >
                          <td className="py-3 px-4 text-gray-300 text-sm">{index + 1}</td>
                          <td className="py-3 px-4">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${admin.isCurrentUser ? 'text-[#f5bb5f]' : 'text-white'}`}>
                                  {admin.name || admin.email}
                                </span>
                                {admin.isCurrentUser && (
                                  <span className="bg-[#f5bb5f] text-black text-xs px-2 py-0.5 rounded font-medium">Вы</span>
                                )}
                              </div>
                              <div className="text-gray-400 text-xs mt-1">{admin.email}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${admin.role === 'superadmin' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}`}>
                              {admin.role === 'superadmin' ? 'Супер-админ' : 'Администратор'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-300 text-sm">
                            {formatDate(admin.createdAt)}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${admin.status === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                              {admin.status === 'active' ? 'Активен' : 'Неактивен'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Статистика */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-[#4b4b4b] p-4 rounded-[10px] border border-[#5b5b5b]">
                    <h3 className="text-gray-400 text-sm mb-1">Всего пользователей</h3>
                    <p className="text-2xl font-bold text-white">{admins.length}</p>
                  </div>
                  <div className="bg-[#4b4b4b] p-4 rounded-[10px] border border-[#5b5b5b]">
                    <h3 className="text-gray-400 text-sm mb-1">Администраторов</h3>
                    <p className="text-2xl font-bold text-blue-400">
                      {admins.filter(a => a.role === 'admin' || !a.role).length}
                    </p>
                  </div>
                  <div className="bg-[#4b4b4b] p-4 rounded-[10px] border border-[#5b5b5b]">
                    <h3 className="text-gray-400 text-sm mb-1">Супер-админов</h3>
                    <p className="text-2xl font-bold text-purple-400">
                      {admins.filter(a => a.role === 'superadmin').length}
                    </p>
                  </div>
                  <div className="bg-[#4b4b4b] p-4 rounded-[10px] border border-[#5b5b5b]">
                    <h3 className="text-gray-400 text-sm mb-1">Активных</h3>
                    <p className="text-2xl font-bold text-green-400">
                      {admins.filter(a => a.status === 'active' || !a.status).length}
                    </p>
                  </div>
                </div>

                {/* Пояснение */}
                <div className="p-4 bg-[#4b4b4b] rounded-[10px] border border-[#5b5b5b]">
                  <div className="flex items-start gap-3">
                    <div className="text-[#f5bb5f] text-lg">ℹ️</div>
                    <div>
                      <h3 className="text-white font-semibold mb-2">Как добавить больше администраторов?</h3>
                      <p className="text-gray-300 text-sm mb-2">
                        Чтобы добавить других пользователей в список администраторов, вам нужно:
                      </p>
                      <ol className="text-gray-300 text-sm list-decimal pl-5 space-y-1">
                        <li>Создать коллекцию <code className="bg-[#3b3b3b] px-1.5 py-0.5 rounded text-xs">admins</code> в Firestore</li>
                        <li>Добавить документы с данными администраторов (email, имя, роль)</li>
                        <li>Или использовать Firebase Authentication для управления доступом</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangePassword;